import React from "react";
import * as Router from "next/router";
import styled from "styled-components";

import * as c from "../constants";
import * as u from "../utils";
import * as gtag from "../utils/gtag";
import * as T from "./Typography";
import * as UserContext from "./UserContext";
import Modal from "./Modal";

export default function UpsellModal(props) {
  const userInfo = React.useContext(UserContext.Context);
  const router = Router.useRouter();
  const modalContent = getUpsellContent(router.pathname, props.type);
  if (!modalContent) return null;

  u.sessionStorage.setItem(c.RETURN_TO, router.asPath);
  const modalProperties = {
    title: modalContent.title,
    image: modalContent.image ?? "edit.jpg",
    imageAltText:
      modalContent.imageAltText ??
      "Image of phone with the AllBright Post screen visible",
    onClose: () => props.setType(false),
    buttonText: modalContent.buttonText ?? "Let's go!",
    onProceed,
    isUpsell: true,
    type: props.type,
  };

  return (
    <Modal {...modalProperties}>
      <S.Container>
        <S.SubHeader>
          <T.H3 weight={"bold"}>{modalContent.subtitle}</T.H3>
        </S.SubHeader>
        {modalContent.description && <T.CXL>{modalContent.description}</T.CXL>}
        {modalContent.benefits && (
          <T.CXL>{modalContent.benefits.map(getBenefit)}</T.CXL>
        )}
      </S.Container>
    </Modal>
  );

  function getBenefit(elm) {
    return <S.Item key={elm}>{elm}</S.Item>;
  }

  function onProceed() {
    const registrationPage = userInfo.id ? "profile" : "register";
    if (props.type === "club") {
      gtag.event({
        name: gtag.GA_EVENT.PLATFORM_ENGAGEMENT,
        category: "Club Registration",
        label: "Upsell modal",
        value: props.type,
      });

      router.push("http://promo.allbrightcollective.com/visit-mayfair/");
    } else {
      gtag.event({
        name: gtag.GA_EVENT.PLATFORM_ENGAGEMENT,
        category: "Digital Registration",
        label: "Upsell modal",
        value: props.type,
      });

      router.push(`/digital/${registrationPage}`);
    }
  }
}

const S = {};

S.Container = styled.article`
  padding: 20px 10px;
  text-align: center;
  @media ${u.device.mobile} {
    padding: 20px;
  }
`;

S.SubHeader = styled.div`
  padding-bottom: 10px;
`;

S.Item = styled.div`
  margin-top: 10px;
`;

function getUpsellContent(path, type) {
  if (type === false) return;
  else if (type === "digital")
    return {
      title: "ALLBRIGHT DIGITAL",
      subtitle: "We’d love to let you in on what’s behind here…",
      benefits: [
        "Want access to all the best events, on demand?",
        "Plus, unlimited courses, content and community benefits?",
        "Unlock more opportunities with AllBright Digital.",
      ],
      buttonText: "Upgrade",
      dismissText: "Maybe later",
    };
  // TODO: OSH
  // else if (type === "club")
  //   return {
  //     title: "ALLBRIGHT MAYFAIR",
  //     subtitle: "This event is exclusively for club members.",
  //     benefits: [
  //       "Interested in becoming one?",
  //       "We’d love to give you a tour...",
  //     ],
  //     buttonText: "Find out more",
  //     dismissText: "Not today, thank you",
  //   };
  else return getCustomisedContent(path)[type];
}

function getCustomisedContent(page) {
  switch (page) {
    case "/edit/articles/[article]":
      return {
        save: {
          title: "Save this story",
          subtitle: "Bookmark unlimited articles to read anytime, anywhere.",
          benefits: [
            "Follow your favourite writers",
            "Enjoy personalised recommendations",
            "Read anytime with our free iOS app",
          ],
        },
        share: {
          title: "Share this story",
          subtitle:
            "Feeling inspired by this feature? Share the love and pass it onto someone in your sisterhood!",
          benefits: [
            "Follow your favourite writers",
            "Enjoy personalised recommendations",
            "Read anytime with our free iOS app",
          ],
        },
        follow: {
          title: "Never miss a story.",
          subtitle:
            "Don’t miss out! Follow your favourite AllBright writers and topics today.",
          benefits: [
            "Save articles to read later",
            "Enjoy personalised recommendations",
            "Read anytime with our free iOS app",
          ],
        },
        comment: {
          title: "Join the conversation",
          subtitle: "Why not build on this story’s ideas with your own?",
          benefits: [
            "Save articles to read later",
            "Follow your favourite writers",
            "Read anytime with our free iOS app",
          ],
        },
      };
    case "/edit/articles":
      return {
        saved: {
          title: "Save this story",
          subtitle: "Bookmark unlimited articles to read anytime, anywhere.",
          benefits: [
            "Follow your favourite writers",
            "Enjoy personalised recommendations",
            "Read anytime with our free iOS app",
          ],
        },
        recommended: {
          title: "Handpicked for you",
          subtitle:
            "Enjoy personalised recommendations for articles we know you'll love.",
          benefits: [
            "Follow your favourite writers",
            "Save articles to read later",
            "Read anytime with our free iOS app",
          ],
        },
        following: {
          title: "Never miss a story",
          subtitle:
            "Don’t miss out! Follow your favourite AllBright writers and topics today.",
          benefits: [
            "Save articles to read anytime, anywhere",
            "Enjoy personalised recommendations",
            "Read anytime with our free iOS app",
          ],
        },
      };
    case "/edit":
      return {
        save: {
          title: "Save this story",
          subtitle: "Bookmark unlimited articles to read anytime, anywhere.",
          benefits: [
            "Follow your favourite writers",
            "Enjoy personalised recommendations",
            "Read anytime with our free iOS app",
          ],
        },
      };
    case "/events/[event]":
      return {
        comment: {
          title: "Join the conversation",
          subtitle:
            "Can't wait to attend this event? Share your thoughts with our global sisterhood!",
          benefits: [
            "Enjoy complimentary events",
            "Connect with like-minded attendees",
            "Watch events on the go with our iOS app",
          ],
        },
        rsvp: {
          title: "RSVP to AllBright events",
          subtitle:
            "Be inspired by our curated selection of events, workshops and discussions",
          benefits: [
            "Connect with like-minded attendees",
            "Watch previous events on-demand",
            "Watch events on the go with our iOS app",
          ],
        },
        play: {
          title: "Watch previous events, on demand.",
          subtitle:
            "Missed an event? No problem! Catch up now with your AllBright Digital Membership",
          benefits: [
            "Connect with like-minded attendees",
            "Attend live events",
            "Watch events on the go with our iOS app",
          ],
        },
      };
    case "/academy":
      return {
        save: {
          title: "Bookmark this course",
          subtitle: "Save Academy courses to watch anytime, anywhere.",
          benefits: [
            "Access 170+ hours of Academy content",
            "Connect with other members watching the course",
            "Watch courses on the go with our free iOS app",
          ],
        },
      };
    case "/academy/my-learning":
      return {
        track: {
          title: "Jump back in",
          subtitle: "Automatically track your Academy progress",
          benefits: [
            "Save Academy courses to watch anytime, anywhere",
            "Connect with other members watching the course",
            "Watch courses on the go with our free iOS app",
          ],
        },
      };
    case "/academy/course/[course]":
      return {
        comment: {
          title: "Join the conversation",
          subtitle:
            "How did you find this course? Share your ideas with your sisterhood!",
          benefits: [
            "Access 170+ hours of Academy courses",
            "Connect with other members watching the course",
            "Watch courses on the go with our free iOS app",
          ],
        },
        watch: {
          title: "Enrol on Academy courses",
          subtitle:
            "Unlock your potential with workshops led by the brightest and best business minds.",
          benefits: [
            "Save courses to return to later",
            "Connect with other members watching the course",
            "Watch courses on the go with our free iOS app",
          ],
        },
      };
  }
}
